import type { CSSProperties } from 'react';

// The unlocked portion of the bar is one continuous dark-to-bright gradient
// that always stretches across whatever is CURRENTLY filled/green, not the
// whole bar — so it reaches full brightness at the current progress edge
// regardless of how few levels are unlocked. The caller (RebateProgressBar)
// computes each segment's [start, end] position within that filled range and
// passes the fractions straight through; this module only samples the fixed
// (possibly non-uniformly-spaced) palette stops at those fractional
// positions, piecewise-linearly interpolating between whichever two stops
// bracket them.
interface GradientStop<T> {
  /** Fractional position along the whole bar, 0 (left) to 1 (right), ascending. */
  position: number;
  value: T;
}

const UNLOCKED_GRADIENT_STOPS: readonly GradientStop<string>[] = [
  { position: 0, value: '#185101' },
  { position: 0.42, value: '#248914' },
  { position: 0.84, value: '#62DD50' },
  { position: 1, value: '#62DD50' },
];
const LOCKED_GRADIENT_STOPS: readonly GradientStop<string>[] = [
  { position: 0, value: '#262626' },
  { position: 0.42, value: '#6B6B6B' },
  { position: 0.84, value: '#B1B1B1' },
  { position: 1, value: '#BABABA' },
];

const lerpNumber = (from: number, to: number, t: number): number => from + (to - from) * t;

const lerpHexColor = (from: string, to: string, t: number): string => {
  const channel = (hex: string, start: number) => parseInt(hex.slice(start, start + 2), 16);
  const toHex = (value: number) => Math.round(value).toString(16).padStart(2, '0');

  const r = lerpNumber(channel(from, 1), channel(to, 1), t);
  const g = lerpNumber(channel(from, 3), channel(to, 3), t);
  const b = lerpNumber(channel(from, 5), channel(to, 5), t);

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/** Samples a piecewise-linear value from (possibly non-uniformly-spaced) stops at a fractional `position` (0..1). */
const sampleGradientStop = <T,>(
  stops: readonly GradientStop<T>[],
  position: number,
  lerp: (from: T, to: T, t: number) => T,
): T => {
  const clamped = Math.min(1, Math.max(0, position));
  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  if (clamped <= firstStop.position) return firstStop.value;
  if (clamped >= lastStop.position) return lastStop.value;

  let index = 0;
  while (index < stops.length - 2 && clamped > stops[index + 1].position) {
    index++;
  }

  const from = stops[index];
  const to = stops[index + 1];
  const t = (clamped - from.position) / (to.position - from.position);

  return lerp(from.value, to.value, t);
};

interface SegmentGradientVars extends CSSProperties {
  '--segment-gradient-start': string;
  '--segment-gradient-end': string;
}

export const getSegmentGradientVars = (start: number, end: number, isUnlocked: boolean): SegmentGradientVars => {
  const stops = isUnlocked ? UNLOCKED_GRADIENT_STOPS : LOCKED_GRADIENT_STOPS;
  return {
    '--segment-gradient-start': sampleGradientStop(stops, start, lerpHexColor),
    '--segment-gradient-end': sampleGradientStop(stops, end, lerpHexColor),
  } as SegmentGradientVars;
};
